import React, { Component } from "react";
import { Badge, Button, Heading, Pane, Text, TextInput, toaster } from "evergreen-ui";

import Request from "../../../util/Api";

// Legacy product tag picker. Mirrors the v2 MultiSelect: search existing tags
// (server-side, by name/slug via the `q` param) or type a new name to create
// one on the fly. Value is an array of tag ids; onChange emits the next array.
// Hits /v2/tags (the same endpoint the v2 admin uses) — there's no legacy tags
// module.
export default class TagsField extends Component {
  constructor(props) {
    super(props);
    this.state = { available: [], input: "", loading: true, creating: false };
    // Monotonic token so a slow earlier search can't clobber a newer one.
    this._searchSeq = 0;
    this._debounce = null;
  }

  componentDidMount() {
    this.loadTags("");
    this.resolveSelected();
  }

  componentWillUnmount() {
    if (this._debounce) clearTimeout(this._debounce);
  }

  // Merge tags into `available` keyed by id. Accumulating (rather than
  // replacing) keeps already-known tags resolvable after a narrower search —
  // otherwise selected chips and the exact-match check would lose tags that
  // dropped out of the latest result page.
  mergeAvailable(tags) {
    this.setState((s) => {
      const byId = new Map(s.available.map((t) => [t.id, t]));
      for (const t of tags || []) byId.set(t.id, t);
      return { available: Array.from(byId.values()) };
    });
  }

  async loadTags(q) {
    const seq = ++this._searchSeq;
    try {
      const res = await Request.get("/v2/tags", null, {
        _start: 0,
        _end: 50,
        _sort: "name",
        _order: "ASC",
        q: q && q.trim() ? q.trim() : undefined,
      });
      if (seq !== this._searchSeq) return; // superseded by a newer keystroke
      this.mergeAvailable(res.data || []);
      this.setState({ loading: false });
    } catch (e) {
      if (seq !== this._searchSeq) return;
      this.setState({ loading: false });
      toaster.danger("Nu am putut incarca etichetele.");
    }
  }

  // Selected ids arrive without their tag objects (the Product structure keeps
  // only ids), so fetch them once to label the chips on edit. Best-effort: a
  // chip falls back to #id until a search surfaces the tag.
  async resolveSelected() {
    const ids = this.selectedIds;
    if (!ids.length) return;
    const tags = await Promise.all(
      ids.map((id) =>
        Request.get("/v2/tags/[id]", { id })
          .then((r) => r.data)
          .catch(() => null)
      )
    );
    this.mergeAvailable(tags.filter(Boolean));
  }

  onInput(value) {
    this.setState({ input: value });
    if (this._debounce) clearTimeout(this._debounce);
    this._debounce = setTimeout(() => this.loadTags(value), 250);
  }

  get selectedIds() {
    return this.props.value || [];
  }

  selectedTags() {
    const byId = new Map(this.state.available.map((t) => [t.id, t]));
    return this.selectedIds.map((id) => byId.get(id) || { id, name: `#${id}` });
  }

  add(id) {
    if (this.selectedIds.includes(id)) return;
    this.props.onChange([...this.selectedIds, id]);
  }

  remove(id) {
    this.props.onChange(this.selectedIds.filter((x) => x !== id));
  }

  async addOrCreate() {
    const name = this.state.input.trim();
    if (!name) return;

    const existing = this.state.available.find(
      (t) => t.name.toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      this.add(existing.id);
      this.setState({ input: "" });
      return;
    }

    this.setState({ creating: true });
    try {
      const res = await Request.post("/v2/tags", null, null, {
        name,
        active: true,
      });
      const tag = res.data;
      this.mergeAvailable([tag]);
      this.setState({ input: "", creating: false });
      this.add(tag.id);
    } catch (e) {
      this.setState({ creating: false });
      toaster.danger(
        e && e.fields && e.fields.slug === "duplicate"
          ? "Exista deja o eticheta cu acest nume."
          : "Eticheta nu a putut fi creata."
      );
    }
  }

  render() {
    const selected = this.selectedTags();
    const input = this.state.input.trim().toLowerCase();
    // The server already narrowed by `q`; the client filter just keeps the
    // visible chips in sync with the box while a debounced search is in flight
    // and hides already-selected tags.
    const suggestions = this.state.available
      .filter((t) => !this.selectedIds.includes(t.id))
      .filter((t) => !input || t.name.toLowerCase().includes(input))
      .slice(0, 20);
    const hasExact = this.state.available.some(
      (t) => t.name.toLowerCase() === input
    );

    return (
      <Pane>
        <Heading size={500} marginBottom={8}>
          Etichete
        </Heading>

        <Pane display="flex" flexWrap="wrap" gap={6} marginBottom={8}>
          {selected.length === 0 && (
            <Text color="muted" size={300}>
              Nicio eticheta selectata.
            </Text>
          )}
          {selected.map((t) => (
            <Badge
              key={t.id}
              color="blue"
              isInteractive
              onClick={() => this.remove(t.id)}
              title="Click pentru a elimina"
            >
              {t.name} ✕
            </Badge>
          ))}
        </Pane>

        <Pane display="flex" gap={6} marginBottom={8}>
          <TextInput
            flex={1}
            width="100%"
            placeholder="Cauta sau scrie o eticheta noua"
            value={this.state.input}
            onChange={(e) => this.onInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                this.addOrCreate();
              }
            }}
          />
          <Button
            type="button"
            appearance="primary"
            isLoading={this.state.creating}
            disabled={!this.state.input.trim()}
            onClick={() => this.addOrCreate()}
          >
            {input && !hasExact ? "Creeaza" : "Adauga"}
          </Button>
        </Pane>

        <Pane display="flex" flexWrap="wrap" gap={6}>
          {this.state.loading ? (
            <Text color="muted" size={300}>
              Se incarca etichetele...
            </Text>
          ) : suggestions.length === 0 ? (
            <Text color="muted" size={300}>
              {input ? "Nicio eticheta gasita." : "Nu exista etichete."}
            </Text>
          ) : (
            suggestions.map((t) => (
              <Badge
                key={t.id}
                color="neutral"
                isInteractive
                onClick={() => this.add(t.id)}
                title="Click pentru a adauga"
              >
                {t.name}
              </Badge>
            ))
          )}
        </Pane>
      </Pane>
    );
  }
}
